import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import { useLocation, useNavigate } from 'react-router-dom';
import PageContainer from '../components/PageContainer';
import { twClasses } from '../styles/tailwind-utils';
import apiService from '../services/apiService';
import userService from '../services/userService';
import { Box, Paper, Tab, Tabs, Typography, TextField, Button, Stack, InputAdornment, Checkbox, FormControlLabel, Grid, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, CircularProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Button as MuiButton, Alert } from '@mui/material';
import {
  Business as BusinessIcon,
  ContactMail as ContactIcon,
  Palette as BrandingIcon,
  Language as SocialIcon,
  Settings as CrmIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Dns as DnsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  QuestionMark as QuestionMarkIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { LoadingState } from '../components/LoadingState';
import MtaStsRecommendation from './admin/MtaStsRecommendation';
import { SelectChangeEvent } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';

interface CognitoProfile extends Record<string, any> {
  'cognito:groups'?: string[];
  email?: string;
  name?: string;
  'custom:role'?: string;
}

interface ClientAssignment {
  id: number;
  user_id: number;
  client_id: number;
  role: string;
  client_name: string;
  created_at?: string;
  updated_at?: string;
}

interface ClientData {
  id: number;
  agency_name: string;
  url?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone_number?: string;
  business_address?: string;
  business_fein?: string;
  logo_url?: string;
  alt_logo_url?: string;
  primary_color_hex_code?: string;
  accent_color_hex_code?: string;
  background_color_hex_code?: string;
  h_1_font?: string;
  h_2_font?: string;
  body_font?: string;
  contact_page_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  linked_in_url?: string;
  twitter_x_url?: string;
  you_tube_channel_url?: string;
  pinterest_url?: string;
  tik_tok_url?: string;
  whats_app_link?: string;
  sending_email_name?: string;
  sending_email_address?: string;
  sending_phone_number?: string;
  crm?: string;
  vcrm_sync?: boolean;
  vacation_crm_user_api_key?: string;
  vacation_crm_system_api_key?: string;
  use_landing_pages?: boolean;
  landing_pages_value?: string;
  landing_pages_tag?: string;
  landing_pages_name?: string;
  other_crm?: string;
  dns_provider?: string;
  mx_record_value?: string;
  spf_record_name?: string;
  dmarc_record_name?: string;
  connect_name?: string;
  connect_value?: string;
  dkim1_name?: string;
  dkim1_value?: string;
  dkim2_name?: string;
  dkim2_value?: string;
  dkim3_name?: string;
  dkim3_value?: string;
  mx_record_name?: string;
  spf_record_value?: string;
  dmarc_record_value?: string;
  connect_db_name?: string;
  connect_db_user?: string;
  connect_db_password?: string;
}

interface DnsValidationResult {
  found: boolean;
  error: string | null;
}

interface DnsValidationResults {
  mx: DnsValidationResult;
  spf: DnsValidationResult;
  dmarc: DnsValidationResult;
  dkim1: DnsValidationResult;
  dkim2: DnsValidationResult;
  dkim3: DnsValidationResult;
  connect: DnsValidationResult;
  landing_pages: DnsValidationResult;
  nameservers: {
    found: boolean;
    error: string | null;
    servers: string[];
  };
}

const socialIcons = {
  facebook_url: {
    name: 'Facebook',
    icon: 'https://tima-unlayer.s3.us-east-1.amazonaws.com/1739208315353-facebook.png'
  },
  instagram_url: {
    name: 'Instagram',
    icon: 'https://tima-unlayer.s3.us-east-1.amazonaws.com/1739208319265-instagram.png'
  },
  linked_in_url: {
    name: 'LinkedIn',
    icon: 'https://tima-unlayer.s3.us-east-1.amazonaws.com/1739208331249-linkedin.png'
  },
  twitter_x_url: {
    name: 'Twitter',
    icon: 'https://tima-unlayer.s3.us-east-1.amazonaws.com/1739208334157-x.png'
  },
  you_tube_channel_url: {
    name: 'YouTube',
    icon: 'https://tima-unlayer.s3.us-east-1.amazonaws.com/1739208337140-youtube.png'
  },
  pinterest_url: {
    name: 'Pinterest',
    icon: 'https://tima-unlayer.s3.us-east-1.amazonaws.com/1739208423566-pinterest.png'
  },
  tik_tok_url: {
    name: 'TikTok',
    icon: 'https://tima-unlayer.s3.us-east-1.amazonaws.com/1739208342240-tiktok.png'
  },
  whats_app_link: {
    name: 'WhatsApp',
    icon: 'https://tima-unlayer.s3.us-east-1.amazonaws.com/1739208536464-whatsapp.png'
  }
};

const Settings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [revealedPasswords, setRevealedPasswords] = useState<Record<string, boolean>>({});
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [originalData, setOriginalData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { selectedClientId, isAdmin } = useUser();
  const auth = useUser();
  const [isOwner, setIsOwner] = useState(false);
  const [customDnsProvider, setCustomDnsProvider] = useState<string>('');
  const [selectedDnsProvider, setSelectedDnsProvider] = useState<string>('');
  const [dnsValidationResults, setDnsValidationResults] = useState<DnsValidationResults | null>(null);
  const [isValidatingDns, setIsValidatingDns] = useState(false);
  const [showCustomDnsProvider, setShowCustomDnsProvider] = useState(false);
  const [hasValidatedDns, setHasValidatedDns] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [sesModalOpen, setSesModalOpen] = useState(false);
  const [sesStatusMessages, setSesStatusMessages] = useState<string[]>([]);
  const [sesError, setSesError] = useState<string | null>(null);
  const statusListRef = useRef<HTMLUListElement>(null);
  const [confirmSesOpen, setConfirmSesOpen] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  // Bumped each time the user clicks "Validate DNS" so child cards
  // (e.g. MtaStsRecommendation) can hook in and revalidate themselves.
  const [validateTrigger, setValidateTrigger] = useState(0);

  const handleValidateDns = async () => {
    if (!selectedClientId) return;

    setIsValidatingDns(true);
    setDnsValidationResults(null);

    try {
      const response = await apiService.get<DnsValidationResults>(`/api/clients/${selectedClientId}/validate-dns`);
      setDnsValidationResults(response.data);
    } catch (err) {
      console.error('[Settings] Error validating DNS:', err);
    } finally {
      setIsValidatingDns(false);
      setValidateTrigger(t => t + 1);
    }
  };

  const handleCreateSesSettings = async () => {
    setSesModalOpen(true);
    setSesStatusMessages([]);
    setSesError(null);
    try {
      // Try to use apiService.post for the call
      const response = await apiService.post(`/api/clients/${selectedClientId}/create-ses-settings`);
      // If the response is not streaming, just show a success message
      setSesStatusMessages([response.data ? 'SES settings created successfully' : 'SES settings created successfully']);
      const clientResponse = await apiService.get<ClientData>(`/api/clients/${selectedClientId}`);
      setClientData(clientResponse.data);
      setOriginalData(clientResponse.data);
    } catch (err: any) {
      setSesError(err?.message || 'Error creating SES settings');
    }
  };

  const isDnsSet = () => {
    if (!clientData) return false;
    return !!(
      clientData.dkim1_name || clientData.dkim2_name || clientData.dkim3_name ||
      clientData.mx_record_name || clientData.spf_record_name || clientData.dmarc_record_name ||
      clientData.connect_name || clientData.landing_pages_name
    );
  };

  const handleCreateSesClick = () => {
    if (isDnsSet()) {
      setConfirmSesOpen(true);
    } else {
      handleCreateSesSettings();
    }
  };

  // Add effect to validate DNS when tab is loaded
  useEffect(() => {
    if (activeTab === 5 && clientData && clientData.url && !hasValidatedDns && !isValidatingDns) {
      setHasValidatedDns(true);
      handleValidateDns();
    }
  }, [activeTab, clientData, handleValidateDns, hasValidatedDns, isValidatingDns]);

  // Reset validation state when client changes
  useEffect(() => {
    setHasValidatedDns(false);
  }, [selectedClientId]);

  // Tab mapping
  const tabMap = {
    'agency-info': 0,
    'contact-info': 1,
    'branding': 2,
    'social-media': 3,
    'crm': 4,
    'dns': 5,
    'connect': 6
  } as const;

  // Reverse tab mapping
  const reverseTabMap = {
    0: 'agency-info',
    1: 'contact-info',
    2: 'branding',
    3: 'social-media',
    4: 'crm',
    5: 'dns',
    6: 'connect'
  } as const;

  // Set initial tab from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabParam in tabMap) {
      setActiveTab(tabMap[tabParam as keyof typeof tabMap]);
    }
  }, [location.search]);

  // Standard DNS providers
  const standardDnsProviders = ["godaddy.com", "cloudflare.com", "aws.amazon.com", "NameCheap", "wixdns.net"] as const;
  type StandardDnsProvider = (typeof standardDnsProviders)[number];

  // Function to check if a value is a standard DNS provider
  const isStandardDnsProvider = (value: string | undefined | null): boolean => {
    if (!value) return false;
    return standardDnsProviders.includes(value as StandardDnsProvider);
  };

  // Function to check if a field should be editable
  const isFieldEditable = (field: string) => {
    if (!isOwner && !isAdmin()) return false;

    if (isAdmin()) return true;

    if (isOwner) {
      // Owner can only edit dns_provider
      return field === 'dns_provider' || field === 'custom_dns_provider';
    }

    return false;
  };

  useEffect(() => {
    const fetchClientData = async () => {
      if (!selectedClientId) return;

      try {
        const clientResponse = await apiService.get<ClientData>(`/api/clients/${selectedClientId}`);
        setClientData(clientResponse.data);
        setOriginalData(clientResponse.data);

        // Check if user is owner
        const userResponse = await userService.getUserInfo();
        console.log('[Settings] User response:', userResponse);
        const assignments = userResponse?.client_assignments || [];
        console.log('[Settings] All assignments:', assignments);
        const clientAssignment = assignments.find((a) => a.client_id === selectedClientId);
        console.log('[Settings] Selected client ID:', selectedClientId);
        console.log('[Settings] Found client assignment:', clientAssignment);
        const isOwnerRole = clientAssignment?.role?.toUpperCase() === 'OWNER';
        console.log('[Settings] Role check:', {
          role: clientAssignment?.role,
          upperCase: clientAssignment?.role?.toUpperCase(),
          isOwner: isOwnerRole
        });
        setIsOwner(isOwnerRole);
      } catch (err) {
        console.error('[Settings] Error fetching client data:', err);
      }
    };

    fetchClientData();
  }, [selectedClientId]);

  useEffect(() => {
    // Set custom DNS provider if it's not in standard list
    const provider = clientData?.dns_provider;
    if (provider) {
      if (isStandardDnsProvider(provider)) {
        setSelectedDnsProvider(provider);
        setShowCustomDnsProvider(false);
      } else {
        setSelectedDnsProvider('Other');
        setCustomDnsProvider(provider);
        setShowCustomDnsProvider(true);
      }
    } else {
      setSelectedDnsProvider('');
      setCustomDnsProvider('');
      setShowCustomDnsProvider(false);
    }
  }, [clientData?.dns_provider]);

  useEffect(() => {
    statusListRef.current?.scrollTo(0, statusListRef.current.scrollHeight);
  }, [sesStatusMessages]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Update URL with new tab
    const newTab = reverseTabMap[newValue as keyof typeof reverseTabMap];
    navigate(`/app/agency-settings?tab=${newTab}`, { replace: true });
  };

  const hasChanges = () => {
    return JSON.stringify(clientData) !== JSON.stringify(originalData);
  };

  const handleCancel = () => {
    setClientData(originalData);
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    if (!clientData || !selectedClientId) return;

    setLoading(true);
    setSuccessMessage(null);

    try {
      await apiService.put(`/api/admin/clients/${selectedClientId}`, clientData);
      setSuccessMessage('Changes saved successfully');
      setOriginalData(clientData);
    } catch (err) {
      // Silently handle save errors
    } finally {
      setLoading(false);
    }
  };

  const handleDnsProviderChange = (event: SelectChangeEvent<string>) => {
    if (!event.target) return;
    const value = event.target.value;
    setSelectedDnsProvider(value);

    if (value === "Other") {
      setShowCustomDnsProvider(true);
      if (clientData) {
        setClientData({ ...clientData, dns_provider: customDnsProvider });
      }
    } else {
      setShowCustomDnsProvider(false);
      if (clientData) {
        setClientData({ ...clientData, dns_provider: value });
      }
      if (value !== customDnsProvider) {
        setCustomDnsProvider('');
      }
    }
  };

  const handleCustomDnsProviderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target) return;
    const value = e.target.value;
    setCustomDnsProvider(value);
    if (value && clientData) {
      setClientData({ ...clientData, dns_provider: value });
    }
  };

  const renderDnsStatusIcon = (result: DnsValidationResult | undefined) => {
    const iconStyle = {
      width: 24,
      height: 24,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1rem'
    };

    if (!result) return (
      <Box sx={{
        ...iconStyle,
        bgcolor: '#ffd54f',
        color: '#fff'
      }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>?</Typography>
      </Box>
    );

    if (isValidatingDns) return (
      <Box sx={{
        ...iconStyle,
        bgcolor: '#2196f3',
        color: '#fff'
      }}>
        <HourglassEmptyIcon className="animate-spin" sx={{ fontSize: 16 }} />
      </Box>
    );

    if (result.found) return (
      <Box sx={{
        ...iconStyle,
        bgcolor: '#4caf50',
        color: '#fff'
      }}>
        <CheckCircleIcon sx={{ fontSize: 16 }} />
      </Box>
    );

    return (
      <Tooltip title={result.error || 'Not found'}>
        <Box sx={{
          ...iconStyle,
          bgcolor: '#f44336',
          color: '#fff'
        }}>
          <CancelIcon sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    );
  };

  // Function to strip domain name from DNS entries for GoDaddy
  const stripDomainFromDnsEntry = (value: unknown, field: string): string => {
    if (!value || typeof value !== 'string' || !clientData?.url) {
      return value?.toString() || '';
    }

    // Case insensitive check for GoDaddy
    if (!clientData?.dns_provider?.toLowerCase()?.includes('godaddy')) {
      return value;
    }

    // For all entries ending with _name, strip the domain
    if (field.endsWith('_name')) {
      // Match and remove .domain.tld pattern (including any leading dot)
      const pattern = `^(.+?)(?:\\.[^.]+\\.[^.]+)?$`;
      const regex = new RegExp(pattern, 'i');

      const match = value.match(regex);
      if (match) {
        return match[1];
      }
    }

    return value;
  };

  const handleCopyToClipboard = (text: unknown) => {
    navigator.clipboard.writeText(String(text || '')).then(() => {
      // Could add a toast notification here if desired
      console.log('Copied to clipboard:', text);
    });
  };

  const handleCopySettings = () => {
    if (!clientData) return;
    const settings = [
      `Agency: ${clientData.agency_name || ''}`,
      `Domain: ${clientData.url || ''}`,
      `DNS Provider: ${clientData.dns_provider || ''}`,
      '',
      `DKIM 1 Name: ${clientData.dkim1_name || ''}`,
      `DKIM 1 Value: ${clientData.dkim1_value || ''}`,
      `DKIM 1 Type: CNAME`,
      '',
      `DKIM 2 Name: ${clientData.dkim2_name || ''}`,
      `DKIM 2 Value: ${clientData.dkim2_value || ''}`,
      `DKIM 2 Type: CNAME`,
      '',
      `DKIM 3 Name: ${clientData.dkim3_name || ''}`,
      `DKIM 3 Value: ${clientData.dkim3_value || ''}`,
      `DKIM 3 Type: CNAME`,
      '',
      `MX Record Name: ${clientData.mx_record_name || ''}`,
      `MX Record Value: ${clientData.mx_record_value || ''}`,
      `MX Record Type: MX`,
      '',
      `SPF Record Name: ${clientData.spf_record_name || ''}`,
      `SPF Record Value: ${clientData.spf_record_value || ''}`,
      `SPF Record Type: TXT`,
      '',
      `DMARC Record Name: ${clientData.dmarc_record_name || ''}`,
      `DMARC Record Value: ${clientData.dmarc_record_value || ''}`,
      `DMARC Record Type: TXT`,
      '',
      `Connect Name: ${clientData.connect_name || ''}`,
      `Connect Value: ${clientData.connect_value || ''}`,
      `Connect Type: A`,
      '',
      `Landing Pages Name: ${clientData.landing_pages_name || ''}`,
      `Landing Pages Value: ${clientData.landing_pages_value || ''}`,
      `Landing Pages Type: CNAME`,
    ].join('\n');
    navigator.clipboard.writeText(settings);
    setCopySnackbarOpen(true);
  };

  // Update renderDnsField to use the domain stripping function and add copy buttons
  const renderDnsField = (
    label: string,
    nameField: keyof ClientData,
    valueField: keyof ClientData,
    resultKey: keyof DnsValidationResults,
    tooltip: string,
    recordType: string = ''
  ) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Tooltip title={tooltip}>
          <InfoIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>
      <Grid container spacing={2} alignItems="center">
        {recordType && (
          <Grid item xs={12} md={1}>
            <Box component="div" sx={{
              bgcolor: 'primary.50',
              color: 'primary.main',
              p: 1,
              borderRadius: 1,
              textAlign: 'center',
              fontSize: '0.75rem',
              fontWeight: 'medium',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>
              {recordType}
            </Box>
          </Grid>
        )}
        <Grid item xs={12} md={recordType ? 4 : 5}>
          <TextField
            fullWidth
            label="Name"
            value={stripDomainFromDnsEntry(clientData?.[nameField], String(nameField)) || ''}
            onChange={(e) => setClientData(clientData ? { ...clientData, [nameField]: e.target.value } : null)}
            disabled={!isAdmin()}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleCopyToClipboard(stripDomainFromDnsEntry(clientData?.[nameField], String(nameField)) || '')}
                    edge="end"
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                '& .MuiInputBase-input': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Value"
            value={clientData?.[valueField] || ''}
            onChange={(e) => setClientData(clientData ? { ...clientData, [valueField]: e.target.value } : null)}
            disabled={!isAdmin()}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleCopyToClipboard(clientData?.[valueField] || '')}
                    edge="end"
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                height: 'auto',
                '& .MuiInputBase-input': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }
            }}
            error={!!(dnsValidationResults?.[resultKey]?.found && dnsValidationResults?.[resultKey]?.error)}
            helperText={dnsValidationResults?.[resultKey]?.found && dnsValidationResults?.[resultKey]?.error ? `Found: ${dnsValidationResults[resultKey].error}` : ''}
          />
        </Grid>
        <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          {renderDnsStatusIcon(dnsValidationResults?.[resultKey])}
        </Grid>
      </Grid>
    </Box>
  );

  const renderTextField = (
    label: string,
    field: keyof ClientData,
    multiline: boolean = false,
    rows: number = 1,
    minHeight?: string,
    includeLink: boolean = false
  ) => (
    <TextField
      fullWidth
      label={label}
      value={clientData?.[field] || ''}
      onChange={(e) => setClientData(clientData ? { ...clientData, [field]: e.target.value } : null)}
      disabled={!isOwner && !isAdmin()}
      multiline={multiline}
      rows={rows}
      variant="outlined"
      sx={{ mb: 2, ...(minHeight ? { '& .MuiInputBase-root': { minHeight } } : {}) }}
      InputProps={{
        endAdornment: includeLink && clientData?.[field] ? (
          <InputAdornment position="end">
            <IconButton
              onClick={() => window.open(String(clientData[field]), '_blank')}
              size="small"
              edge="end"
            >
              <OpenInNewIcon />
            </IconButton>
          </InputAdornment>
        ) : undefined
      }}
    />
  );

  const renderPasswordField = (label: string, field: keyof ClientData) => {
    const revealed = !!revealedPasswords[field as string];
    return (
      <TextField
        fullWidth
        label={label}
        value={clientData?.[field] || ''}
        onChange={(e) => setClientData(clientData ? { ...clientData, [field]: e.target.value } : null)}
        disabled={!isOwner && !isAdmin()}
        type={revealed ? 'text' : 'password'}
        variant="outlined"
        sx={{ mb: 2 }}
        InputProps={{
          // disable browser password-manager prompts on this admin form
          autoComplete: 'new-password',
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() =>
                  setRevealedPasswords((prev) => ({ ...prev, [field as string]: !prev[field as string] }))
                }
                size="small"
                edge="end"
                aria-label={revealed ? 'Hide password' : 'Show password'}
                title={revealed ? 'Hide password' : 'Show password'}
              >
                {revealed ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  };

  const renderSocialField = (field: keyof typeof socialIcons) => (
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth
        label={socialIcons[field].name}
        value={clientData?.[field] || ''}
        onChange={(e) => setClientData(clientData ? { ...clientData, [field]: e.target.value } : null)}
        disabled={!isOwner && !isAdmin()}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <img src={socialIcons[field].icon} alt={socialIcons[field].name} style={{ width: 24, height: 24 }} />
            </InputAdornment>
          ),
          endAdornment: clientData?.[field] && (
            <InputAdornment position="end">
              <IconButton
                onClick={() => window.open(String(clientData[field]), '_blank')}
                size="small"
                edge="end"
              >
                <OpenInNewIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );

  const renderColorField = (label: string, field: keyof ClientData) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <TextField
        label={label}
        value={clientData?.[field] || ''}
        onChange={(e) => setClientData(clientData ? { ...clientData, [field]: e.target.value } : null)}
        disabled={!isOwner && !isAdmin()}
        variant="outlined"
        size="small"
        fullWidth
      />
      <Box
        sx={{
          width: '100%',
          height: '40px',
          borderRadius: '4px',
          border: '1px solid #ccc',
          backgroundColor: (clientData?.[field] as string) || '#fff',
        }}
      />
    </Box>
  );

  return (
    <PageContainer>
      <Typography
        variant="subtitle1"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Agency Settings
      </Typography>

      <Paper elevation={2} className="p-6">
        {successMessage && (
          <div className={`${twClasses.alert} ${twClasses.alertSuccess} mb-4`}>
            <p>{successMessage}</p>
            <button onClick={() => setSuccessMessage(null)} className="text-gray-700 hover:text-gray-900">
              Dismiss
            </button>
          </div>
        )}

        {loading ? (
          <LoadingState message="Loading settings..." />
        ) : clientData ? (
          <>
            <Paper elevation={0} className="mb-4">
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                className="border-b border-gray-200"
              >
                <Tab icon={<BusinessIcon />} iconPosition="start" label="Agency Info" />
                <Tab icon={<ContactIcon />} iconPosition="start" label="Contact Info" />
                <Tab icon={<BrandingIcon />} iconPosition="start" label="Branding" />
                <Tab icon={<SocialIcon />} iconPosition="start" label="Social Media" />
                <Tab icon={<CrmIcon />} iconPosition="start" label="CRM" />
                <Tab icon={<DnsIcon />} iconPosition="start" label="DNS" />
                {isAdmin() && <Tab icon={<CrmIcon />} iconPosition="start" label="Connect" />}
              </Tabs>
            </Paper>

            <Box className="p-4">
              {/* Agency Info Tab */}
              {activeTab === 0 && (
                <Stack spacing={2}>
                  {renderTextField('Agency Name', 'agency_name')}
                  {renderTextField('Website URL', 'url')}
                  {renderTextField('Contact Name', 'contact_name')}
                  {renderTextField('Contact Email', 'contact_email')}
                  {renderTextField('Contact Phone', 'contact_phone_number')}
                  {renderTextField('Business FEIN', 'business_fein')}
                  {renderTextField('Business Address', 'business_address', true, 3, '100px')}
                </Stack>
              )}

              {/* Contact Info Tab */}
              {activeTab === 1 && (
                <Stack spacing={4}>
                  <Box>
                    <Box
                      sx={{
                        mb: 4,
                        p: 2,
                        bgcolor: '#fff3e0',
                        borderRadius: 1,
                        border: 1,
                        borderColor: '#ffb74d',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                      }}
                    >
                      <WarningIcon sx={{ color: '#f57c00', mt: 0.5 }} fontSize="small" />
                      <Typography variant="body2" sx={{ color: '#795548' }}>
                        These settings control how your contact information appears on emails and landing pages.
                      </Typography>
                    </Box>
                    <Stack spacing={2}>
                      {renderTextField('Sending Email Name', 'sending_email_name')}
                      {renderTextField('Sending Email Address', 'sending_email_address')}
                      {renderTextField('Sending Phone Number', 'sending_phone_number')}
                      {renderTextField('Contact Page URL', 'contact_page_url', false, 1, undefined, true)}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Landing Pages</Typography>
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={clientData?.use_landing_pages || false}
                            onChange={(e) => setClientData(clientData ? { ...clientData, use_landing_pages: e.target.checked } : null)}
                            disabled={!isOwner && !isAdmin()}
                          />
                        }
                        label="Use Landing Pages"
                      />
                      <TextField
                        fullWidth
                        label="Landing Pages URL"
                        value={clientData?.landing_pages_value || ''}
                        disabled={true}
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                </Stack>
              )}

              {/* Branding Tab */}
              {activeTab === 2 && (
                <Stack spacing={4}>
                  <Box>
                    <Box
                      sx={{
                        mb: 4,
                        p: 2,
                        bgcolor: '#fff3e0',
                        borderRadius: 1,
                        border: 1,
                        borderColor: '#ffb74d',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                      }}
                    >
                      <WarningIcon sx={{ color: '#f57c00', mt: 0.5 }} fontSize="small" />
                      <Typography variant="body2" sx={{ color: '#795548' }}>
                        These settings control how your contact information appears on emails and landing pages.
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Logos</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        {renderTextField('Logo URL', 'logo_url')}
                        {clientData.logo_url && (
                          <Box sx={{ mt: 1, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                            <img src={clientData.logo_url} alt="Logo" style={{ maxWidth: '100%', height: 'auto' }} />
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderTextField('Alternative Logo URL', 'alt_logo_url')}
                        {clientData.alt_logo_url && (
                          <Box sx={{ mt: 1, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                            <img src={clientData.alt_logo_url} alt="Alternative Logo" style={{ maxWidth: '100%', height: 'auto' }} />
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Colors</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        {renderColorField('Primary Color', 'primary_color_hex_code')}
                      </Grid>
                      <Grid item xs={4}>
                        {renderColorField('Accent Color', 'accent_color_hex_code')}
                      </Grid>
                      <Grid item xs={4}>
                        {renderColorField('Background Color', 'background_color_hex_code')}
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Fonts</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        {renderTextField('H1 Font', 'h_1_font')}
                      </Grid>
                      <Grid item xs={4}>
                        {renderTextField('H2 Font', 'h_2_font')}
                      </Grid>
                      <Grid item xs={4}>
                        {renderTextField('Body Font', 'body_font')}
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              )}

              {/* Social Media Tab */}
              {activeTab === 3 && (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      mb: 4,
                      p: 2,
                      bgcolor: '#fff3e0',
                      borderRadius: 1,
                      border: 1,
                      borderColor: '#ffb74d',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1
                    }}
                  >
                    <WarningIcon sx={{ color: '#f57c00', mt: 0.5 }} fontSize="small" />
                    <Typography variant="body2" sx={{ color: '#795548' }}>
                      These settings control how your contact information appears on emails and landing pages.
                    </Typography>
                  </Box>
                  {renderSocialField('facebook_url')}
                  {renderSocialField('instagram_url')}
                  {renderSocialField('linked_in_url')}
                  {renderSocialField('twitter_x_url')}
                  {renderSocialField('you_tube_channel_url')}
                  {renderSocialField('pinterest_url')}
                  {renderSocialField('tik_tok_url')}
                  {renderSocialField('whats_app_link')}
                </Stack>
              )}

              {/* CRM Tab */}
              {activeTab === 4 && (
                <Stack spacing={2}>
                  <Box>
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel>CRM</InputLabel>
                        <Select
                          value={clientData?.crm || ''}
                          label="CRM"
                          onChange={(e) => setClientData(clientData ? { ...clientData, crm: e.target.value } : null)}
                          disabled={!isOwner && !isAdmin()}
                        >
                          <MenuItem value="VCRM">VCRM</MenuItem>
                          <MenuItem value="Travel Joy">Travel Joy</MenuItem>
                          <MenuItem value="Client Base">Client Base</MenuItem>
                          <MenuItem value="Tres">Tres</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>

                      {clientData?.crm && clientData.crm !== 'VCRM' && (
                        <Box sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: '#e3f2fd',
                          borderRadius: 1,
                          border: 1,
                          borderColor: '#90caf9',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1
                        }}>
                          <InfoIcon sx={{ color: '#1976d2', mt: 0.5 }} fontSize="small" />
                          <Typography variant="body2" sx={{ color: '#1976d2' }}>
                            An upcoming release will provide the ability for the user to change the Do Not Contact setting for Contacts. Until that is provided, please send a monthly csv file update containing fields with columns: email address and do-not-contact, where do-not-contact is (1 = do not contact, 0 = contact) to support@travelminds.com. Name the file <strong>{clientData.agency_name?.toLowerCase().replace(/\s+/g, '-')}.donotcontact.yyyymmdd.csv</strong>.
                            <Box component="p" sx={{ mt: 2, mb: 0 }}>
                              To get a list of unsubscribed contacts, go to the Contacts page, Filter By Segment 'unsubscribed', and Export CSV.
                            </Box>
                          </Typography>
                        </Box>
                      )}

                      {clientData?.crm === 'Other' && (
                        <TextField
                          fullWidth
                          label="Other CRM Name"
                          value={clientData?.other_crm || ''}
                          onChange={(e) => setClientData(clientData ? { ...clientData, other_crm: e.target.value } : null)}
                          disabled={!isOwner && !isAdmin()}
                          variant="outlined"
                        />
                      )}

                      {clientData?.crm === 'VCRM' && (
                        <>
                          <Box sx={{
                            mb: 2,
                            p: 2,
                            bgcolor: '#fff3e0',
                            borderRadius: 1,
                            border: 1,
                            borderColor: '#ffb74d',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1
                          }}>
                            <WarningIcon sx={{ color: '#f57c00', mt: 0.5 }} fontSize="small" />
                            <Typography variant="body2" sx={{ color: '#795548' }}>
                              Enabling Synchronization with VacationCRM will do a one-time update to override the General Marketing flag in VacationCRM for all profiles to match the unsubscribe state in the Marketing Portal. For most VacationCRM users, this flag is unused. If you know you use the General Marketing flag, contact support@travelminds.com to coordinate this initial update. After this one-time update, you can check or uncheck the General Marketing flag to subscribe or unsubscribe users in the Marketing Portal
                              <br />
                              Updates happen overnight and will be reflected in all systems the next business day.
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={clientData?.vcrm_sync || false}
                                onChange={(e) => setClientData(clientData ? { ...clientData, vcrm_sync: e.target.checked } : null)}
                                disabled={!isOwner && !isAdmin()}
                              />
                            }
                            label="Synchronize Contacts with VacationCRM"
                          />
                          <Box sx={{
                            mb: 2,
                            p: 2,
                            bgcolor: '#e3f2fd',
                            borderRadius: 1,
                            border: 1,
                            borderColor: '#90caf9'
                          }}>
                            <Typography variant="body2" sx={{ color: '#1976d2' }}>
                              This key lets the Marketing Portal send do-not-contact updates to VacationCRM to set the General Marketing flag.
                              <br />To create a User API key:
                              <ol style={{ marginTop: '8px', marginLeft: '20px', listStyleType: 'decimal' }}>
                                <li>Login to Vacation CRM as the administrator</li>
                                <li>Go to Settings, System Settings, Company Settings</li>
                                <li>Scroll down to "Api Keys" (not the System API Key field)</li>
                                <li>Create a Key, name it "TravelMinds"</li>
                                <li>Look for the new TravelMinds key in the list, click the middle icon under the Operate column to copy the key</li>
                                <li>Paste it here</li>
                              </ol>
                            </Typography>
                          </Box>
                          {renderTextField('User API Key', 'vacation_crm_user_api_key')}

                        </>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}

              {/* DNS Tab */}
              {activeTab === 5 && (
                <Stack spacing={2}>
                  {/* Warning Message */}
                  <Box>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: '#fff3e0',
                        borderRadius: 1,
                        border: 1,
                        borderColor: '#ffb74d',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <WarningIcon sx={{ color: '#f57c00', mt: 0.5 }} fontSize="small" />
                      <Typography variant="body2" sx={{ color: '#795548' }}>
                        DNS settings are critical for your email delivery and domain functionality. Incorrect changes can disrupt your email service. Please ensure you understand the purpose of each record before making changes.
                      </Typography>
                    </Box>
                  </Box>

                  {/* DNS Records */}
                  <Box>
                    {/* DNS Provider Section */}
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      sx={{ mb: 3 }}
                    >
                      <Grid item xs={12} md={5}>
                        <FormControl fullWidth>
                          <InputLabel>DNS Provider</InputLabel>
                          <Select
                            value={selectedDnsProvider}
                            label="DNS Provider"
                            onChange={handleDnsProviderChange}
                            disabled={!isFieldEditable('dns_provider')}
                            size="small"
                          >
                            {standardDnsProviders.map((provider) => (
                              <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                            ))}
                            {customDnsProvider && !isStandardDnsProvider(customDnsProvider) && (
                              <MenuItem key={customDnsProvider} value={customDnsProvider}>{customDnsProvider}</MenuItem>
                            )}
                            <MenuItem key="other" value="Other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={7} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {showCustomDnsProvider && selectedDnsProvider === 'Other' ? (
                          <TextField
                            fullWidth
                            label="Custom DNS Provider"
                            value={customDnsProvider}
                            onChange={handleCustomDnsProviderChange}
                            disabled={!isFieldEditable('custom_dns_provider')}
                            variant="outlined"
                            size="small"
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<ContentCopyIcon />}
                              onClick={handleCopySettings}
                              disabled={!clientData?.url}
                            >
                              Copy Settings
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleValidateDns}
                              disabled={isValidatingDns || !clientData?.url}
                              startIcon={isValidatingDns ? <CircularProgress size={20} color="inherit" /> : <DnsIcon />}
                            >
                              {isValidatingDns ? 'Validating...' : 'Validate DNS'}
                            </Button>
                            {isAdmin() && (
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleCreateSesClick}
                                disabled={!clientData?.url}
                              >
                                Create SES Settings
                              </Button>
                            )}
                          </Box>
                        )}
                      </Grid>
                    </Grid>

                    {/* Name Servers Section */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2">Name Servers</Typography>
                        <Tooltip title="The name servers that are currently configured for your domain">
                          <InfoIcon fontSize="small" color="action" />
                        </Tooltip>
                      </Box>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          {isValidatingDns ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={20} thickness={4} />
                              <Typography variant="body2">Checking name servers...</Typography>
                            </Box>
                          ) : dnsValidationResults?.nameservers?.found ? (
                            <Box sx={{
                              p: 2,
                              bgcolor: 'background.paper',
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'divider'
                            }}>
                              <Stack spacing={1}>
                                {dnsValidationResults.nameservers.servers.map((server, index) => (
                                  <Box key={index} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1,
                                    bgcolor: 'background.default',
                                    borderRadius: 1
                                  }}>
                                    <CheckCircleIcon color="success" fontSize="small" />
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {server}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyToClipboard(server)}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          ) : (
                            <Box sx={{
                              p: 2,
                              bgcolor: 'background.paper',
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <CancelIcon color="error" fontSize="small" />
                              <Typography variant="body2">
                                {dnsValidationResults?.nameservers?.error || 'Not found'}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>

                    {renderDnsField(
                      'MX Records',
                      'mx_record_name',
                      'mx_record_value',
                      'mx',
                      'Mail Exchange (MX) records',
                      'MX'
                    )}

                    {renderDnsField(
                      'SPF Records',
                      'spf_record_name',
                      'spf_record_value',
                      'spf',
                      'Sender Policy Framework (SPF) records',
                      'TXT'
                    )}

                    {renderDnsField(
                      'DMARC Records',
                      'dmarc_record_name',
                      'dmarc_record_value',
                      'dmarc',
                      'Domain-based Message Authentication, Reporting & Conformance (DMARC) records',
                      'TXT'
                    )}

                    {/* DKIM Records */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="subtitle2">DKIM Records</Typography>
                        <Tooltip title="DomainKeys Identified Mail (DKIM) records add a digital signature to your emails">
                          <InfoIcon fontSize="small" color="action" />
                        </Tooltip>
                      </Box>
                      {['1', '2', '3'].map((num) => (
                        <Box key={num} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2">DKIM {num}</Typography>
                          </Box>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={1}>
                              <Box component="div" sx={{
                                bgcolor: 'primary.50',
                                color: 'primary.main',
                                p: 1,
                                borderRadius: 1,
                                textAlign: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 'medium',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em'
                              }}>
                                CNAME
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Name"
                                value={stripDomainFromDnsEntry(clientData?.[`dkim${num}_name` as keyof ClientData], `dkim${num}_name`) || ''}
                                onChange={(e) => setClientData(clientData ? { ...clientData, [`dkim${num}_name`]: e.target.value } : null)}
                                disabled={!isAdmin()}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => handleCopyToClipboard(stripDomainFromDnsEntry(clientData?.[`dkim${num}_name` as keyof ClientData], `dkim${num}_name`) || '')}
                                        edge="end"
                                        size="small"
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                  sx: {
                                    '& .MuiInputBase-input': {
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Value"
                                value={clientData?.[`dkim${num}_value` as keyof ClientData] || ''}
                                onChange={(e) => setClientData(clientData ? { ...clientData, [`dkim${num}_value`]: e.target.value } : null)}
                                disabled={!isAdmin()}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => handleCopyToClipboard(clientData?.[`dkim${num}_value` as keyof ClientData] || '')}
                                        edge="end"
                                        size="small"
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                  sx: {
                                    height: 'auto',
                                    '& .MuiInputBase-input': {
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                              {renderDnsStatusIcon(dnsValidationResults?.[`dkim${num}` as keyof DnsValidationResults])}
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>

                    {renderDnsField(
                      'Connect Records',
                      'connect_name',
                      'connect_value',
                      'connect',
                      'The Connect URL is the ESP platform location',
                      'A'
                    )}

                    {renderDnsField(
                      'Landing Pages',
                      'landing_pages_name',
                      'landing_pages_value',
                      'landing_pages',
                      'DNS settings for configuring landing page subdomains',
                      'CNAME'
                    )}

                    <MtaStsRecommendation
                      domain={clientData?.url || null}
                      clientId={selectedClientId}
                      validateTrigger={validateTrigger}
                    />
                  </Box>
                </Stack>
              )}

              {/* Connect Tab - Only visible to admins */}
              {activeTab === 6 && isAdmin() && (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      mb: 4,
                      p: 2,
                      bgcolor: '#e3f2fd',
                      borderRadius: 1,
                      border: 1,
                      borderColor: '#90caf9',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1
                    }}
                  >
                    <InfoIcon sx={{ color: '#1976d2', mt: 0.5 }} fontSize="small" />
                    <Typography variant="body2" sx={{ color: '#0d47a1' }}>
                      This tab contains advanced connection settings for the agency. These settings are used by the system for database connections and landing pages configuration.
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" gutterBottom>Landing Pages</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(clientData?.use_landing_pages)}
                          onChange={(e) => setClientData(clientData ? {
                            ...clientData,
                            use_landing_pages: e.target.checked
                          } : null)}
                          disabled={!isAdmin()}
                        />
                      }
                      label="Enable Landing Pages"
                    />

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={4}>
                        {renderTextField('Landing Pages Tag', 'landing_pages_tag')}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {renderTextField('Landing Pages Name', 'landing_pages_name')}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {renderTextField('Landing Pages Value', 'landing_pages_value')}
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="subtitle1" gutterBottom>Connect Settings</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        {renderTextField('Connect Name', 'connect_name')}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderTextField('Connect Value', 'connect_value')}
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="subtitle1" gutterBottom>Database Connection</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        {renderTextField('Database Name', 'connect_db_name')}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {renderTextField('Database User', 'connect_db_user')}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {renderPasswordField('Database Password', 'connect_db_password')}
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              )}

              {/* Action Buttons */}
              {(isOwner || isAdmin()) && (
                <Box className="flex justify-end space-x-4 mt-6 pt-4 border-t border-gray-200">
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={loading || !hasChanges()}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || !hasChanges()}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <LoadingState message="Loading settings..." />
        )}
      </Paper>

      <Dialog open={sesModalOpen} onClose={() => setSesModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create SES Settings</DialogTitle>
        <DialogContent>
          {sesError && <Alert severity="error">{sesError}</Alert>}
          <List ref={statusListRef} sx={{ maxHeight: 300, overflow: 'auto' }}>
            {sesStatusMessages.map((msg, i) => (
              <ListItem key={i}><ListItemText primary={msg} /></ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSesModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmSesOpen} onClose={() => setConfirmSesOpen(false)}>
        <DialogTitle>Confirm Overwrite</DialogTitle>
        <DialogContent>DNS settings already exist. Proceeding will overwrite them. Continue?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSesOpen(false)}>Cancel</Button>
          <Button onClick={() => { setConfirmSesOpen(false); handleCreateSesSettings(); }} color="primary">Continue</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2500}
        onClose={() => setCopySnackbarOpen(false)}
        message="Settings copied to clipboard!"
      />
    </PageContainer>
  );
};

export default Settings;
